import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi, ApiError } from '../services/authApi';
import '../styles/ResetPassword.css';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isValidLink, setIsValidLink] = useState(false);
  const [encryptedUserId, setEncryptedUserId] = useState('');
  const [timestamp, setTimestamp] = useState('');

  useEffect(() => {
    const encryptedUserIdParam = searchParams.get('encryptedUserId');
    const timestampParam = searchParams.get('timestamp');
    
    if (encryptedUserIdParam && timestampParam) {
      setEncryptedUserId(encryptedUserIdParam);
      setTimestamp(timestampParam);
      setIsValidLink(true);
    } else {
      setError(t('resetPassword.error.invalidLink') || 'Invalid reset link. Please request a new password reset.');
    }
  }, [searchParams, t]);

  const validatePassword = (password: string): string | null => {
    if (!password) {
      return t('resetPassword.error.emptyPassword') || 'Password is required';
    }
    
    if (password.length < 8) {
      return t('resetPassword.error.passwordTooShort') || 'Password must be at least 8 characters long';
    }
    
    // Add more password validation rules as needed
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      return t('resetPassword.error.passwordWeak') || 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.error.passwordMismatch') || 'Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await authApi.resetPassword({
        encryptedUserId,
        timestamp,
        newPassword
      });

      // Show success message
      setSuccess(t('resetPassword.success') || 'Password has been reset successfully. You can now login with your new password.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Reset password failed:', error);
      
      if (error instanceof ApiError) {
        if (error.status === 400) {
          setError(
            t('resetPassword.error.linkExpired') || 
            'Password reset link has expired. Please request a new one.'
          );
        } else {
          setError(error.message);
        }
      } else {
        setError(
          t('resetPassword.error.networkError') || 'An unexpected error occurred. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidLink) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-form-wrapper">
          <div className="reset-password-header">
            <div className="logo-bg">
              <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
            </div>
            <h1>{t('resetPassword.invalidTitle') || 'Invalid Link'}</h1>
          </div>
          
          <div className="error-message">{error}</div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="login-button" 
              onClick={() => navigate('/forgot-password')}
            >
              {t('resetPassword.requestNew') || 'Request New Reset Link'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-form-wrapper">
        <div className="reset-password-header">
          <div className="logo-bg">
            <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
          </div>
          <h1>{t('resetPassword.title') || 'Reset Password'}</h1>
          <p>{t('resetPassword.subtitle') || 'Enter your new password below.'}</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <form className="reset-password-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword">{t('resetPassword.newPassword') || 'New Password'}</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('resetPassword.newPasswordPrompt') || 'Enter your new password'}
              disabled={isLoading}
              autoFocus
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('resetPassword.confirmPassword') || 'Confirm Password'}</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('resetPassword.confirmPasswordPrompt') || 'Confirm your new password'}
              disabled={isLoading}
            />
          </div>
          
          <div className="password-requirements">
            <p>{t('resetPassword.requirements') || 'Password requirements:'}</p>
            <ul>
              <li>{t('resetPassword.requirement1') || 'At least 8 characters long'}</li>
              <li>{t('resetPassword.requirement2') || 'Contains uppercase and lowercase letters'}</li>
              <li>{t('resetPassword.requirement3') || 'Contains at least one number'}</li>
            </ul>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="reset-password-button" 
              disabled={isLoading || !!success}
            >
              {isLoading ? 
                t('resetPassword.resetting') || 'Resetting...' : 
                success ? 
                  t('resetPassword.redirecting') || 'Redirecting to login...' :
                  t('resetPassword.resetButton') || 'Reset Password'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
